import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import type { CompletedActivity } from '@/types';
import { useUnitSystem } from '@/hooks/useUnitSystem';
import { useQuery } from '@tanstack/react-query';
import { fetchActivityStreams } from '@/lib/api';
import { createChartTimeTickFormatter } from '@/lib/chart-time';
import { processActivityStreamsForCharts } from '@/lib/stream-processing';

interface ActivityChartsProps {
  activity: CompletedActivity;
}

export function ActivityCharts({ activity }: ActivityChartsProps) {
  const { convertPace, convertElevation, unitSystem } = useUnitSystem();
  
  // Fetch activity streams to get real time-series data
  // Skip fetch for manual activities - they never have GPS/HR streams
  const canHaveStreams = activity.source === 'strava' || activity.source === 'garmin';
  const { data: streamsData, isLoading: isLoadingStreams, error: streamsError } = useQuery({
    queryKey: ['activityStreams', activity.id],
    queryFn: () => fetchActivityStreams(activity.id),
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    enabled: !!activity.id && canHaveStreams,
  });
  
  // Process stream data - only use real backend API data
  const data = useMemo(
    () => processActivityStreamsForCharts(streamsData),
    [streamsData]
  );
  
  // Check if we have real data from streams
  const hasData = data.length > 0;
  
  // Check which metrics we have from real stream data
  const hasHeartRate = useMemo(() => {
    return data.some((point) => point.heartRate !== undefined && point.heartRate > 0);
  }, [data]);
  
  const hasPaceData = useMemo(() => {
    return data.some((point) => point.pace !== undefined && point.pace > 0);
  }, [data]);
  
  const hasPower = useMemo(() => {
    return data.some((point) => point.power !== undefined && point.power > 0);
  }, [data]);
  
  const hasElevation = useMemo(() => {
    return data.some((point) => point.elevation !== undefined && point.elevation !== 0);
  }, [data]);
  
  const hasCadence = useMemo(() => {
    return data.some((point) => point.cadence !== undefined && point.cadence > 0);
  }, [data]);
  
  // Convert pace data for display
  const paceData = useMemo(() => {
    return data
      .filter((point) => point.pace !== undefined && point.pace > 0) // Filter out invalid pace values
      .map((point) => {
        const converted = convertPace(point.pace!);
        return {
          ...point,
          paceDisplay: converted.value,
          paceUnit: converted.unit,
        };
      });
  }, [data, convertPace]);
  
  // Convert elevation data for display
  const elevationDataDisplay = useMemo(() => {
    return data.map((point) => {
      const converted = convertElevation(point.elevation);
      return {
        ...point,
        elevationDisplay: converted.value,
        elevationUnit: converted.unit,
      };
    });
  }, [data, convertElevation]);

  // Show loading state while fetching streams
  if (isLoadingStreams) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-muted-foreground">Loading activity data...</p>
      </div>
    );
  }

  if (!hasData) {
    const errorMessage = streamsError instanceof Error ? streamsError.message : '';
    const isServerError = errorMessage.includes('Server error') || errorMessage.includes('Failed to fetch');
    const isNotAvailable = errorMessage.includes('not available') || errorMessage.includes('404');

    return (
      <div className="text-center py-12 text-muted-foreground">
        <p className="text-sm font-medium">
          {!canHaveStreams ? 'No time-series data' : 'No stream data available'}
        </p>
        {!canHaveStreams ? (
          <p className="text-xs mt-2">
            Route and stream data are only available for Strava and Garmin activities.
          </p>
        ) : isServerError ? (
          <p className="text-xs mt-2">
            An error occurred while fetching stream data. Please try refreshing the page.
          </p>
        ) : isNotAvailable ? (
          <p className="text-xs mt-2">
            Stream data is not available for this activity. This may be due to activity type or API limitations.
          </p>
        ) : (
          <p className="text-xs mt-2">Stream data may not be available for this activity</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Heart Rate Chart */}
      {hasHeartRate && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground/50">Heart Rate</span>
            <span className="text-[10px] font-mono text-muted-foreground/40">bpm</span>
          </div>
          <div className="h-32 bg-card/30 rounded border border-border/20 p-1.5">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                <defs>
                  <linearGradient id="hrGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-4))" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="hsl(var(--chart-4))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" strokeOpacity={0.3} />
                <XAxis
                  dataKey="timeLabel"
                  tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))', opacity: 0.5 }}
                  axisLine={{ stroke: 'hsl(var(--border))', strokeOpacity: 0.3 }}
                  tickLine={false}
                  tickFormatter={createChartTimeTickFormatter(data)}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={['dataMin - 10', 'dataMax + 10']}
                  tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))', opacity: 0.5 }}
                  axisLine={false}
                  tickLine={false}
                  width={28}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '4px',
                    fontSize: '10px',
                    padding: '4px 8px',
                  }}
                  formatter={(value: number) => [`${value}`, 'HR']}
                />
                <Area
                  type="monotone"
                  dataKey="heartRate"
                  stroke="hsl(var(--chart-4))"
                  fill="url(#hrGradient)"
                  strokeWidth={1.5}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Pace Chart */}
      {hasPaceData && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] uppercase tracking-widest text-muted-foreground/50">Pace</span>
            <span className="text-[10px] font-mono text-muted-foreground/40">{unitSystem === 'imperial' ? '/mi' : '/km'}</span>
          </div>
          <div className="h-32 bg-card/30 rounded border border-border/20 p-1.5">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={paceData} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
                <defs>
                  <linearGradient id="paceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" strokeOpacity={0.3} />
                <XAxis
                  dataKey="timeLabel"
                  tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))', opacity: 0.5 }}
                  axisLine={{ stroke: 'hsl(var(--border))', strokeOpacity: 0.3 }}
                  tickLine={false}
                  tickFormatter={createChartTimeTickFormatter(paceData)}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={['dataMin - 0.5', 'dataMax + 0.5']}
                  tick={{ fontSize: 9, fill: 'hsl(var(--muted-foreground))', opacity: 0.5 }}
                  axisLine={false}
                  tickLine={false}
                  width={28}
                  tickFormatter={(value) => value.toFixed(1)}
                  reversed
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '4px',
                    fontSize: '10px',
                    padding: '4px 8px',
                  }}
                  formatter={(value: number, name: string, props: { payload: { paceUnit: string } }) => {
                    const unit = props.payload.paceUnit || (unitSystem === 'imperial' ? 'min/mi' : 'min/km');
                    return [`${value.toFixed(2)} ${unit}`, 'Pace'];
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="paceDisplay"
                  stroke="hsl(var(--chart-1))"
                  fill="url(#paceGradient)"
                  strokeWidth={1.5}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Power Chart */}
      {hasPower && (
        <div>
          <h5 className="text-sm font-medium text-foreground mb-3">Power</h5>
          <div className="h-48 bg-muted/30 rounded-lg p-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="powerGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-3))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--chart-3))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="timeLabel"
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                  tickLine={false}
                  tickFormatter={createChartTimeTickFormatter(data)}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={['dataMin - 20', 'dataMax + 20']}
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={false}
                  tickLine={false}
                  width={35}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  formatter={(value: number) => [`${value} W`, 'Power']}
                />
                <Area
                  type="monotone"
                  dataKey="power"
                  stroke="hsl(var(--chart-3))"
                  fill="url(#powerGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Cadence Chart */}
      {hasCadence && (
        <div>
          <h5 className="text-sm font-medium text-foreground mb-3">Cadence</h5>
          <div className="h-48 bg-muted/30 rounded-lg p-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="cadenceGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-5))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--chart-5))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="timeLabel"
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                  tickLine={false}
                  tickFormatter={createChartTimeTickFormatter(data)}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={['dataMin - 5', 'dataMax + 5']}
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={false}
                  tickLine={false}
                  width={35}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  formatter={(value: number) => [`${value} rpm`, 'Cadence']}
                />
                <Area
                  type="monotone"
                  dataKey="cadence"
                  stroke="hsl(var(--chart-5))"
                  fill="url(#cadenceGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Elevation Chart */}
      {hasElevation && (
        <div>
          <h5 className="text-sm font-medium text-foreground mb-3">Elevation</h5>
          <div className="h-48 bg-muted/30 rounded-lg p-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={elevationDataDisplay} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="elevationGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="timeLabel"
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                  tickLine={false}
                  tickFormatter={createChartTimeTickFormatter(elevationDataDisplay)}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={['dataMin - 10', 'dataMax + 10']}
                  tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={false}
                  tickLine={false}
                  width={35}
                  tickFormatter={(value: number) => {
                    return `${value.toFixed(0)}`;
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                  formatter={(value: number, name: string, props: { payload: { elevationUnit: string } }) => {
                    const unit = props.payload.elevationUnit || (unitSystem === 'imperial' ? 'ft' : 'm');
                    return [`${value.toFixed(1)} ${unit}`, 'Elevation'];
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="elevationDisplay"
                  stroke="hsl(var(--chart-2))"
                  fill="url(#elevationGradient)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
